import { useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Section } from "./Section";
import { useLang } from "@/context/LanguageContext";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { KeyRound, UserCheck, UserPlus, MailWarning, Send, Loader2, Languages, Eye, EyeOff } from "lucide-react";

const EMAIL_LANGS = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
];

const VARS = {
  passwordReset: ["{newPassword}", "{firstname_employee}", "{lastname_employee}", "{displayName_employee}", "{upn_employee}", "{firstname_contact}", "{lastname_contact}", "{displayName_contact}"],
  guestInvite: ["{displayName_guest}"],
  initialPassword: ["{password}", "{displayName_employee}", "{upn_employee}", "{mail_employee}", "{firstname_employee}", "{lastname_employee}", "{firstname_contact}", "{lastname_contact}", "{displayName_contact}"],
  sharedMailboxAutoReply: ["{displayName}", "{upn}", "{firstname}", "{lastname}", "{mail}"],
};

// Sample data used for the live preview so admins see the final email before sending.
const SAMPLE = {
  passwordReset: { newPassword: "P@ssw0rd-Sample-1234", firstname_employee: "Jane", lastname_employee: "Doe", displayName_employee: "Jane Doe", upn_employee: "jane.doe@contoso.com", firstname_contact: "John", lastname_contact: "Manager", displayName_contact: "John Manager" },
  guestInvite: { displayName_guest: "Sample Guest" },
  initialPassword: { password: "P@ssw0rd-Sample-1234", displayName_employee: "Jane Doe", upn_employee: "jane.doe@contoso.com", mail_employee: "jane.doe@contoso.com", firstname_employee: "Jane", lastname_employee: "Doe", firstname_contact: "John", lastname_contact: "Manager", displayName_contact: "John Manager" },
  sharedMailboxAutoReply: { displayName: "Jane Doe", upn: "jane.doe@contoso.com", firstname: "Jane", lastname: "Doe", mail: "jane.doe@contoso.com" },
};

const renderTpl = (tpl, ctx) => (tpl || "").replace(/\{(\w+)\}/g, (m, k) => (ctx[k] !== undefined ? ctx[k] : m));

function VarHints({ vars }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {vars.map((v) => <code key={v} className="rounded-sm bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">{v}</code>)}
    </div>
  );
}

// One multilanguage template editor: language tabs + subject + body + live preview, bound to s.emailTemplates[type][lang].
function TemplateEditor({ s, setS, type, testid, bodyOnly = false }) {
  const { t } = useLang();
  const [lang, setLang] = useState(s.defaultLanguage || "en");
  const [showPreview, setShowPreview] = useState(false);
  const node = (s.emailTemplates && s.emailTemplates[type]) || {};
  const val = node[lang] || {};
  const setField = (field, v) => {
    const next = {
      ...(s.emailTemplates || {}),
      [type]: { ...node, [lang]: { ...(node[lang] || {}), [field]: v } },
    };
    setS({ ...s, emailTemplates: next });
  };
  const previewSubject = renderTpl(val.subject || "", SAMPLE[type]);
  const previewBody = renderTpl(val.body || "", SAMPLE[type]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testid}-lang-tabs`}>
        {EMAIL_LANGS.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            data-testid={`${testid}-lang-${l.code}`}
            className={`rounded-sm border px-3 py-1 text-xs font-semibold transition-colors ${lang === l.code ? "border-primary bg-primary text-white" : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"}`}
          >
            {l.label}{l.code === (s.defaultLanguage || "en") ? ` · ${t("set.emailLangDefault")}` : ""}
          </button>
        ))}
        <button type="button" onClick={() => setShowPreview((p) => !p)} data-testid={`${testid}-preview-toggle`} className="ml-auto inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-1 text-xs font-semibold text-zinc-600 transition-colors hover:bg-zinc-50">
          {showPreview ? <EyeOff size={13} /> : <Eye size={13} />} {showPreview ? t("set.tplPreviewHide") : t("set.tplPreview")}
        </button>
      </div>
      <div className={showPreview ? "grid gap-4 md:grid-cols-2" : ""}>
        <div className="space-y-3">
          {!bodyOnly && (
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.tplSubject")}</Label>
              <Input value={val.subject || ""} onChange={(e) => setField("subject", e.target.value)} data-testid={`${testid}-subject-${lang}`} className="rounded-sm border-zinc-300 font-mono text-sm" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.tplBody")}</Label>
            <Textarea value={val.body || ""} onChange={(e) => setField("body", e.target.value)} rows={7} data-testid={`${testid}-body-${lang}`} className="rounded-sm border-zinc-300 font-mono text-sm" />
            <VarHints vars={VARS[type]} />
          </div>
        </div>
        {showPreview && (
          <div className="space-y-1.5" data-testid={`${testid}-preview`}>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.tplPreview")}</Label>
            <div className="rounded-sm border border-zinc-200 bg-white shadow-sm">
              {!bodyOnly && (
                <div className="border-b border-zinc-100 px-4 py-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("set.tplSubject")}</div>
                  <div className="text-sm font-semibold text-zinc-900" data-testid={`${testid}-preview-subject`}>{previewSubject || <span className="text-zinc-300">—</span>}</div>
                </div>
              )}
              <div className="whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed text-zinc-700" data-testid={`${testid}-preview-body`}>{previewBody || <span className="text-zinc-300">—</span>}</div>
            </div>
            <p className="text-[11px] text-zinc-400">{t("set.tplSampleNote")}</p>
          </div>
        )}
      </div>
      <TestEmail type={type} lang={lang} testid={`${testid}-test`} />
    </div>
  );
}

function TestEmail({ type, lang, testid }) {
  const { t } = useLang();
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.includes("@")) { toast.error(t("set.tplTestNeedEmail")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/messages/test", { type, to, lang });
      toast.success(`${t("set.tplTestSent")} (${data.message})`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 border-t border-zinc-100 pt-4">
      <div className="flex-1 space-y-1.5" style={{ minWidth: 220 }}>
        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.tplTestRecipient")}</Label>
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@company.com" data-testid={`${testid}-to`} className="rounded-sm border-zinc-300 font-mono text-sm" />
      </div>
      <Button variant="outline" onClick={send} disabled={busy} data-testid={`${testid}-send`} className="rounded-sm border-zinc-300">
        {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />} {t("set.tplTest")}
      </Button>
    </div>
  );
}

export function MessagesSection({ s, setS }) {
  const { t } = useLang();
  return (
    <>
      <Section icon={Languages} title={t("set.emailLangTitle")}>
        <p className="mb-3 text-xs text-zinc-400">{t("set.emailLangDesc")}</p>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="settings-default-language">
          {EMAIL_LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setS({ ...s, defaultLanguage: l.code })}
              data-testid={`settings-default-language-${l.code}`}
              className={`rounded-sm border px-4 py-1.5 text-sm font-semibold transition-colors ${(s.defaultLanguage || "en") === l.code ? "border-primary bg-primary text-white" : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </Section>
      <Section icon={KeyRound} title={t("set.tplPwTitle")}>
        <p className="mb-3 text-xs text-zinc-400">{t("set.tplPwDesc")}</p>
        <TemplateEditor s={s} setS={setS} type="passwordReset" testid="tpl-password-reset" />
      </Section>
      <Section icon={UserPlus} title={t("set.tplInitPwTitle")}>
        <p className="mb-3 text-xs text-zinc-400">{t("set.tplInitPwDesc")}</p>
        <TemplateEditor s={s} setS={setS} type="initialPassword" testid="tpl-initial-password" />
      </Section>
      <Section icon={UserCheck} title={t("set.tplGuestTitle")}>
        <p className="mb-3 text-xs text-zinc-400">{t("set.tplGuestDesc")}</p>
        <TemplateEditor s={s} setS={setS} type="guestInvite" testid="tpl-guest-invite" />
      </Section>
      <Section icon={MailWarning} title={t("set.tplAutoReplyTitle")}>
        <p className="mb-3 text-xs text-zinc-400">{t("set.tplAutoReplyDesc")}</p>
        <TemplateEditor s={s} setS={setS} type="sharedMailboxAutoReply" testid="tpl-auto-reply" bodyOnly />
      </Section>
    </>
  );
}
